import { useUserBundleAccess, useUserCourseAccess } from "@/hooks";
import type { User, UserBundleAccess, UserCourseAccess } from "@/types";
import {
  mapBackendStatusToDisplayStatus,
  mapDisplayStatusToColor,
} from "@/types/models/userBundleAccesses";
import { Badge, Loader } from "@/ui";
import { formatDateToDDMMYYYY } from "@/utils";
import { mapPlanNameToLabel } from "@/utils/planUtils";

const dateOrNull = (value: Date | null): string =>
  value ? formatDateToDDMMYYYY(new Date(value)) : "—";

const freeAccessLabel = (source?: "admin" | "partner" | null): string => {
  if (source === "partner") {
    return "Free — from partner";
  }
  if (source === "admin") {
    return "Free — from admin";
  }
  return "Free (gifted)";
};

const GrantRow = ({
  kind,
  name,
  status,
  approvedOn,
  approvedBy,
  startDate,
  endDate,
}: {
  kind: string;
  name: string;
  status: UserBundleAccess["status"];
  approvedOn: Date | null;
  approvedBy?: string | null;
  startDate: Date | null;
  endDate: Date | null;
}) => {
  const givenLabel = approvedBy ? `Given by ${approvedBy} (admin)` : "Given";

  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3">
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-x-1.5">
          <span className="text-xs font-medium uppercase tracking-wide text-neutral-400">
            {kind}
          </span>
          <span className="text-sm font-medium text-neutral-900">{name}</span>
        </div>
        <span className="text-xs text-neutral-500">
          {`${givenLabel}: ${dateOrNull(approvedOn)} · Access from: ${dateOrNull(startDate)} · ${
            endDate
              ? `Expires: ${formatDateToDDMMYYYY(new Date(endDate))}`
              : "No expiry"
          }`}
        </span>
      </div>
      <Badge
        text={mapBackendStatusToDisplayStatus(status)}
        color={mapDisplayStatusToColor(status)}
      />
    </div>
  );
};

export const UserAccessTab = ({ user }: { user: User }) => {
  const { useUserBundleAccessesForUser } = useUserBundleAccess();
  const { useUserCourseAccessesForUser } = useUserCourseAccess();

  const { data: bundleData, isLoading: isLoadingBundles } =
    useUserBundleAccessesForUser(user.id);
  const { data: courseData, isLoading: isLoadingCourses } =
    useUserCourseAccessesForUser(user.id);

  const bundles: UserBundleAccess[] = bundleData?.data ?? [];
  const courses: UserCourseAccess[] = courseData?.data ?? [];
  const isLoading = isLoadingBundles || isLoadingCourses;

  const seen = new Set<string>();
  const plans = (user.plans ?? []).filter((plan) => {
    if (!plan.name || seen.has(plan.name)) {
      return false;
    }
    seen.add(plan.name);
    return true;
  });

  return (
    <div className="flex flex-col gap-8 pt-6">
      {/* App subscriptions */}
      <section className="flex flex-col gap-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
          App subscriptions
        </span>
        {plans.length === 0 ? (
          <p className="text-sm text-neutral-500">No subscriptions</p>
        ) : (
          <div className="flex flex-col divide-y divide-neutral-100 rounded-xl border border-neutral-200">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className="flex items-center justify-between px-4 py-3"
              >
                <span className="text-sm font-medium text-neutral-900">
                  {mapPlanNameToLabel(plan.name)}
                </span>
                <Badge
                  text={
                    plan.free_access
                      ? freeAccessLabel(plan.granted_source)
                      : "Paid"
                  }
                  color={plan.free_access ? "warning" : "success"}
                />
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Granted courses & materials */}
      <section className="flex flex-col gap-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">
          Courses &amp; materials granted
        </span>
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader />
          </div>
        ) : bundles.length === 0 && courses.length === 0 ? (
          <p className="text-sm text-neutral-500">
            No courses or materials granted
          </p>
        ) : (
          <div className="flex flex-col divide-y divide-neutral-100 rounded-xl border border-neutral-200">
            {bundles.map((access) => (
              <GrantRow
                key={access.id}
                kind="Bundle"
                name={access.bundle?.name ?? "—"}
                status={access.status}
                approvedOn={access.approved_on}
                approvedBy={access.approved_by}
                startDate={access.access_start_date}
                endDate={access.access_end_date}
              />
            ))}
            {courses.map((access) => (
              <GrantRow
                key={access.id}
                kind="Course"
                name={access.course?.name ?? "—"}
                status={access.status}
                approvedOn={access.approved_on}
                approvedBy={access.approved_by}
                startDate={access.access_start_date}
                endDate={access.access_end_date}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
